import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { sectionRenderer, buildSectionSelector } from '@theme/section-renderer';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    // FIX: this used to call history.back() here to pop the state
    // pushed in #handleHistoryOpen whenever the drawer was closed —
    // including ordinary closes via the X button, the backdrop, or
    // Escape. history.back() fires an async popstate event, and on
    // mobile the browser's own automatic scroll restoration for that
    // popstate was landing the page back at the top, racing against
    // (and beating) any scroll position we tried to restore
    // ourselves.
    //
    // history.back() is only actually needed for the real
    // hardware/gesture back-button case — and that case never reaches
    // this method at all, because the browser itself already pops the
    // entry and fires popstate, which #handlePopState (below) picks
    // up directly to close the dialog. For every other close (X
    // button, backdrop click, Escape), all we need to do is clear the
    // `cartDrawerOpen` flag so a *later* real back-button press
    // doesn't think the drawer is still open — replaceState() does
    // exactly that without firing popstate or touching scroll at all,
    // so the page simply stays exactly where it was.
    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open, announces count,
   * and refreshes every cart-items-component on the page.
   *
   * The `instanceof CartAddEvent` guard is required because
   * CartAddEvent/CartUpdateEvent share the same underlying event type
   * string — without it, this fired on every plain quantity change too
   * and double-morphed the section.
   *
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { sections?: Record<string, string>, didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent)) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    if (event.detail?.data?.didError) return;

    this.#refreshCartItemsComponents(event.detail?.data?.sections);
  };

  /**
   * @param {Record<string, string> | undefined} sections
   */
  #refreshCartItemsComponents(sections) {
    const elements = document.querySelectorAll('cart-items-component');

    elements.forEach(async (el) => {
      if (!(el instanceof HTMLElement)) return;

      const sectionId = el.dataset.sectionId;
      if (!sectionId) return;

      const isDrawer = el.dataset.drawer !== undefined;

      let html = sections?.[sectionId];

      if (!html) {
        try {
          html = await sectionRenderer.getSectionHTML(sectionId, false);
        } catch (err) {
          console.error('[cart-drawer] getSectionHTML failed', err);
          return;
        }
      }

      try {
        const fragment = new DOMParser().parseFromString(html, 'text/html');

        // Find the matching cart-items-component in the freshly rendered
        // HTML — must match both section id AND drawer/non-drawer, in
        // case the response contains more than one instance.
        const candidates = fragment.querySelectorAll(`cart-items-component[data-section-id="${sectionId}"]`);
        let newEl = null;
        for (const candidate of candidates) {
          if ((candidate.dataset.drawer !== undefined) === isDrawer) {
            newEl = candidate;
            break;
          }
        }
        if (!newEl) newEl = candidates[0] || fragment.querySelector('cart-items-component');
        if (!newEl) return;

        // FIX: switched from morphSection's `mode: 'hydration'` (which
        // only patches content inside [data-hydration-key] elements via
        // a separate, more limited code path — morphHydrationByKey) to
        // calling morph() directly with the theme's own MORPH_OPTIONS —
        // the exact same call already used everywhere else a section
        // gets fully re-rendered. MORPH_OPTIONS' onAfterUpdate hook
        // automatically calls `node.updatedCallback()` for every
        // Component instance touched by the morph, which is the
        // framework's real, proven mechanism for keeping `refs` fresh
        // after a DOM change — this is what quantity +/-/remove depend
        // on to find the right button/input elements. The hydration-key
        // path appeared to leave the drawer visually correct but with
        // +/-/remove non-functional until a full page reload; this uses
        // the same trusted path a fresh page load itself relies on.
        morph(el, newEl, { ...MORPH_OPTIONS, childrenOnly: true });

        // morph() (unlike morphSection()) doesn't handle the section's
        // scoped <style data-section-stylesheet> tag on its own —
        // replicate that step manually so the design still comes
        // through correctly on this path too.
        const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
        if (newStylesheet) {
          const sectionWrapper = document.getElementById(buildSectionSelector(sectionId));
          const existingStylesheet = sectionWrapper?.querySelector('style[data-section-stylesheet]');
          if (existingStylesheet) {
            existingStylesheet.textContent = newStylesheet.textContent;
          } else if (sectionWrapper) {
            sectionWrapper.prepend(newStylesheet.cloneNode(true));
          }
        }
      } catch (err) {
        console.error('[cart-drawer] morph failed', err);
      }
    });
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}